import type { SxProps, Theme } from "@mui/material";
import { type FC, type ReactNode, useCallback, useMemo, useState } from "react";
import { SystemFlatSwitch } from "../../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import type { ManagementCenterHoursResponse, ManagementCenterHPPDResponse, ManagementCenterPPDResponse } from "../../../utils/models";
import type { ClampRange } from "../utils";
import { HoursOverTime } from "./HoursOverTime";
import { HPPDOverTime } from "./HPPDOverTime";
import { PPDOverTime } from "./PPDOverTime";

type MetricType = "ppd" | "hppd" | "hours";

const switchOptions = [
	{ label: "PPD", value: "ppd" as MetricType },
	{ label: "HPPD", value: "hppd" as MetricType },
	{ label: "Hours", value: "hours" as MetricType },
];

type Props = {
	ppdData?: ManagementCenterPPDResponse;
	hppdData?: ManagementCenterHPPDResponse;
	hoursData?: ManagementCenterHoursResponse;
	isLoadingPpd: boolean;
	isLoadingHppd: boolean;
	isLoadingHours: boolean;
	hidePpd?: boolean;
	trailing?: ReactNode;
	containerSx?: SxProps<Theme>;
	shrinkLegendForSmallScreens?: boolean;
	/** Selected range; clamps edge weekly buckets so the chart never plots a point outside the selection. */
	clampRange?: ClampRange;
};

export const PPDHPPDOverTime: FC<Props> = ({
	ppdData,
	hppdData,
	hoursData,
	isLoadingPpd,
	isLoadingHppd,
	isLoadingHours,
	hidePpd,
	trailing,
	containerSx,
	shrinkLegendForSmallScreens,
	clampRange,
}) => {
	const { track } = useAnalytics();
	const [metric, setMetric] = useState<MetricType>(hidePpd ? "hppd" : "ppd");

	const handleSwitch = useCallback(
		(option: { value: MetricType }) => {
			setMetric(option.value);
			track("management_center_ppd_hppd_toggle", { metric: option.value });
		},
		[track],
	);

	const activeOptions = useMemo(() => (hidePpd ? switchOptions.filter((option) => option.value !== "ppd") : switchOptions), [hidePpd]);

	const switchControl = (
		<fieldset style={{ border: "none", margin: 0, padding: 0 }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
			<SystemFlatSwitch
				options={activeOptions}
				activeIndexOrValue={metric}
				onSwitch={handleSwitch}
				fontSizeOverrides={{ xs: "10px", xl: "12px", xxl: "12px" }}
				maxWidthOverrides={{ xs: "60px", xl: "60px", xxl: "60px" }}
			/>
		</fieldset>
	);

	const combinedTrailing = (
		<>
			{switchControl}
			{trailing}
		</>
	);

	if (metric === "hours") {
		return (
			<HoursOverTime
				data={hoursData}
				isLoading={isLoadingHours}
				trailing={combinedTrailing}
				containerSx={containerSx}
				shrinkLegendForSmallScreens={shrinkLegendForSmallScreens}
				clampRange={clampRange}
			/>
		);
	}

	if (metric === "hppd") {
		return (
			<HPPDOverTime
				data={hppdData}
				isLoading={isLoadingHppd}
				trailing={combinedTrailing}
				containerSx={containerSx}
				shrinkLegendForSmallScreens={shrinkLegendForSmallScreens}
				clampRange={clampRange}
			/>
		);
	}

	return (
		<PPDOverTime
			data={ppdData}
			isLoading={isLoadingPpd}
			trailing={combinedTrailing}
			containerSx={containerSx}
			shrinkLegendForSmallScreens={shrinkLegendForSmallScreens}
			clampRange={clampRange}
		/>
	);
};
